import { OrderFieldConfig, OrderFieldsRequest, OrderFormSection } from './../../core/classes/orderFieldConfig';
import { Component, Input, Output, OnInit, ViewChild, EventEmitter, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAccordion } from '@angular/material/expansion';
import { PropertyName, SectionName } from 'src/app/core/enumerations/sectionNames';
import { Constants, HotKeysHelper } from '../constants';
import { Subject } from 'rxjs';
import { LocalStorageService } from '../services/local-storage.service';
import { BroadcastService } from '../services/broadcast.service';
import { DoctorsDayDetailsDto } from 'src/app/core/classes/doctorsDayDetailsDto';

@Component({
    selector: 'app-order-dynamic',
    templateUrl: './order-dynamic.component.html',
    styleUrls: ['./order-dynamic.component.scss'],
    standalone: false
})
export class OrderDynamicComponent implements OnInit, AfterViewInit {
  @ViewChild(MatAccordion) accordion: MatAccordion;
  @Input() fields: OrderFormSection[] = [];
  @Input() showOptionButtons: boolean;
  @Input() isEditMode: boolean;
  @Output() isEditModeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() addSpecimen = new EventEmitter();
  @Output() editModeCancel = new EventEmitter();
  @Output() isAllCollapsed = new EventEmitter();
  @Input() parentOrderForm: any;
  @Input() orderFormPreferences: OrderFormSection[] = [];
  @Output() validateSpecimenForm: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() batchSpecimen = new EventEmitter();
  @Input() config: { [key: string]: any } = {};
  @Output() TestsProfilesSelectionChange: EventEmitter<any> = new EventEmitter<any>();

  form: UntypedFormGroup;
  addMargin: boolean;
  orderFormRequest = new OrderFieldsRequest();
  helpTextForSections: { [k: string]: any } = {};
  inputValuechange: boolean;
  panelToBeFocused: string;
  htmlElement = new Subject<HTMLElement>();
  batchButtonName = 'Site Code';
  isSpecimenInformationValid = false;
  constructor(private readonly fb: UntypedFormBuilder, private readonly cdr: ChangeDetectorRef, private readonly localStorageService: LocalStorageService, 
              public broadcastService: BroadcastService) { }

  get value() {
    return this.form;
  }

  ngOnInit() {
    this.form = this.createControl();
    this.assignHelpText();
    this.prefillDoctorsDayDetailSections();
    this.assignBatchSpecimenButtonName();
    this.subscribeToSpecimenInformationChanges();
    this.subscribeToTestsProfilesSelectionChange();
  }

  prefillDoctorsDayDetailSections() {
    this.broadcastService.getDoctorDayDetailsDto().subscribe(res => {
      const doctorsDayDetailsDto = res;
      this.prefillClientIdentifier(doctorsDayDetailsDto);
      this.prefillPatientDonorInformation(doctorsDayDetailsDto);
    });
  }

  private prefillClientIdentifier(doctorsDayDetailsDto: DoctorsDayDetailsDto) {
    if (!this.form.controls['Client Identifier']) {
      return;
    }
    
    const clientIdentifierControls = (this.form.controls['Client Identifier'] as UntypedFormArray).controls;
    const id2Control = this.getControlByName(clientIdentifierControls, 'AdditionalID#2');
    const id3Control = this.getControlByName(clientIdentifierControls, 'AdditionalID#3');
    const id4Control = this.getControlByName(clientIdentifierControls, 'AdditionalID#4');

    if (id2Control) { id2Control.setValue(doctorsDayDetailsDto.secondaryId); }
    if (id3Control) { id3Control.setValue(doctorsDayDetailsDto.tertiaryId); }
    if (id4Control) { id4Control.setValue(doctorsDayDetailsDto.quaternaryId); }
  }

  private prefillPatientDonorInformation(doctorsDayDetailsDto: DoctorsDayDetailsDto) {
    if (!this.form.controls['Patient/Donor Information']) {
      return;
    }

    const patientDonorInfoControls = (this.form.controls['Patient/Donor Information'] as UntypedFormArray).controls;
    const dobControl = this.getControlByName(patientDonorInfoControls, 'DOB');
    
    if (!dobControl) {
      return;
    }

    if (Object.keys(doctorsDayDetailsDto).length > 0) {
      const dobValue = doctorsDayDetailsDto.dob == null ? doctorsDayDetailsDto.dob : new Date(doctorsDayDetailsDto.dob);
      dobControl.setValue(dobValue);
    }

    const genderControl = this.getControlByName(patientDonorInfoControls, 'Gender');
    if (genderControl) {
      genderControl.setValue(doctorsDayDetailsDto.gender);
    }
  }

  getControlByName(controls: AbstractControl[], name: string): AbstractControl|null {
    for ( const control of controls) {
      if(control.get(name)) {
        return control.get(name);
      }
    }
    return null;
  }

  ngAfterViewInit(): void {
    this.accordion.openAll();
    this.isAllCollapsed.emit(false);
  }

  createControl() {
    this.orderFormRequest = JSON.parse(this.localStorageService.getItem(Constants.ORDER_FORM_REQUEST));
    const group = this.fb.group({});
    this.fields.forEach(field => {
      const formArray = new UntypedFormArray([]);
      for (const key of field.values) {
        const innergroup = this.fb.group({});
        if (key.type === 'CheckBox') {
          const checkBoxControl = key.options.map(c => new UntypedFormControl(false), this.bindValidations(key));
          if (key.labelCode === PropertyName.COPY_TO_ACCOUNT) {
            this.setValueForPackageAccount(key, checkBoxControl);
          }
          innergroup.addControl(key.labelCode, new UntypedFormArray(checkBoxControl));
          formArray.push(innergroup);
        } else {
          const control = this.fb.control(key.value, this.bindValidations(key));
          innergroup.addControl(key.labelCode, control);
          formArray.push(innergroup);
        }
      }
      group.addControl(field.sectionName, formArray);
    });
    return group;
  }

  setValueForPackageAccount(key: OrderFieldConfig, checkBoxControl: UntypedFormControl[]) {
    const packageAccountCode = this.orderFormRequest.packageAccountCode;
    if (this.orderFormRequest.orderAccountCode != packageAccountCode) {
      const packageAccountIndex = key.options.findIndex(acc => acc.code === packageAccountCode);
      if (packageAccountIndex > -1) {
        checkBoxControl[packageAccountIndex].setValue(this.orderFormRequest.packageAccountCode)
      }
    }
  }

  bindValidations(field: any) {
    let validList = [];
    switch (field.type) {
      case 'NumberBox':
        if (field.inputType === 'Integer' || field.inputType === 'Float') {
          if (field.validations !== null && field.validations.length > 0) {
            validList = [...validList, ...this.checkValidationsForValidators(field.validations, field.labelCode, field.inputType)];
          }
        }
        break;
      case 'TextBox':
      case 'DatePicker':
      case 'TimePicker':
      case 'DateTimePicker':
      case 'SelectSearchBoxApi':
        if (field.validations !== null && field.validations.length > 0) {
          validList = [...validList, ...this.checkValidationsForValidators(field.validations)];
        }
        break;
      case 'DropDown':
        if (field.validations !== null && field.validations.length > 0) {
          validList = [...validList, ...this.checkValidationsForValidators(field.validations, field.type)];
        }
        break;
    }
    return Validators.compose(validList) || null;
  }

  checkValidationsForValidators(validations, type?, inputType?) {
    const validList = [];
    validations.forEach(valid => {
      switch (valid.name) {
        case 'rangeValidator':
          validations = valid.validator.split('-');
          validList.push(Validators.min(validations[0]), Validators.max(validations[1]));
          break;
        case 'required':
          validList.push(Validators.required);
          break;
        case 'maxLength':
          if (type !== 'DropDown' && inputType !== 'Float') {
            validList.push(Validators.maxLength(valid.validator));
          }
          break;
        case 'pattern':
          if (type !== 'GAUS') {
            validList.push(Validators.pattern(valid.validator.replace(/\\\\/g, '\\')));
          }
          break;
        default:
          break;
      }
    });
    return validList;
  }

  addSpecimenFn(sectionName) {
    this.addSpecimen.emit({
      formValue: (this.form.get(sectionName) as UntypedFormArray).getRawValue(),
      isEditMode: this.isEditMode
    });
  }

  batchSpecimenFn(sectionName) {
    this.batchSpecimen.emit({
      formValue: (this.form.get(sectionName) as UntypedFormArray).getRawValue(),
      isEditMode: this.isEditMode,
      buttonName: this.batchButtonName
    });
  }

  isEditModeChangeFn(sectionName) {
    this.isEditModeChange.emit(false);
    this.editModeCancel.emit({
      formValue: (this.form.get(sectionName) as UntypedFormArray).getRawValue()
    });
  }

  valueChanges(event: any) {
    this.validateSpecimenForm.emit(event);
  }

  assignBatchSpecimenButtonName()
  {
    this.fields.forEach(x => {
      if (x.sectionName === SectionName.SPECIMEN_INFORMATION) {
        for (const key of x.values) {
       if(key.labelCode === PropertyName.SampleSource)
       {
         this.batchButtonName = key.labelName;
       }
      }
    }
    });
  }

  assignHelpText() {
    this.fields.forEach(x => {
      switch (x.sectionName) {
        case SectionName.CLIENT_IDENTIFIER:
          this.helpTextForSections[SectionName.CLIENT_IDENTIFIER] = HotKeysHelper.CLIENT_IDENTIFIER_HELPTEXT;
          break;
        case SectionName.PATIENT_DONOR_INFORMATION:
          this.helpTextForSections[SectionName.PATIENT_DONOR_INFORMATION] = HotKeysHelper.PATIENT_DONOR_INFORMATION_HELPTEXT;
          break;
        case SectionName.ORDER_INFROMATION:
          this.helpTextForSections[SectionName.ORDER_INFROMATION] = HotKeysHelper.ORDER_INFORMATION_HELPTEXT;
          break;
        case SectionName.PROFILES:
          this.helpTextForSections[SectionName.PROFILES] = HotKeysHelper.PROFILES_HELPTEXT;
          break;
        case SectionName.SINGLE_TEST:
          this.helpTextForSections[SectionName.SINGLE_TEST] = HotKeysHelper.TESTS_SINGLE_TEST_HELPTEXT;
          break;
        case SectionName.TESTS:
          this.helpTextForSections[SectionName.TESTS] = HotKeysHelper.TESTS_SINGLE_TEST_HELPTEXT;
          break;
        case SectionName.COPY_TO_ACCOUNT:
          this.helpTextForSections[SectionName.COPY_TO_ACCOUNT] = HotKeysHelper.COPY_TO_ACCOUNT_HELPTEXT;
          break;
        case SectionName.REFLEX_RULES:
          this.helpTextForSections[SectionName.REFLEX_RULES] = HotKeysHelper.REFLEX_RULES_HELPTEXT;
          break;
        case SectionName.BILLING_RULES:
          this.helpTextForSections[SectionName.BILLING_RULES] = HotKeysHelper.BILLING_RULES_HELPTEXT;
          break;
        case SectionName.SPECIMEN_INFORMATION:
          this.helpTextForSections[SectionName.SPECIMEN_INFORMATION] = HotKeysHelper.SPECIMEN_INFORMATION_HELPTEXT;
          break;
      }
    });
  }
  
  subscribeToSpecimenInformationChanges(): void {
    if(this.form) {
      const sectionName = SectionName.SPECIMEN_INFORMATION;
      const formArray = this.form.get(sectionName) as UntypedFormArray;
      if (formArray) {
        // Iterate over each control in the formArray
        formArray.controls.forEach((control, index) => {
          control.valueChanges.subscribe(() => {
            this.isSpecimenInformationValid = this.checkSpecimenInformationValidity(formArray);
          });
        });
      }
    }
  }  
  
  checkSpecimenInformationValidity(formArray: UntypedFormArray): boolean {
    // Iterate through the controls in the formArray and check their status
    return formArray.controls.every((control) => {
      const controlValue = control.value;
  
      // Skip validation for "SampleSource"
      if (controlValue && controlValue.hasOwnProperty('SampleSource')) {
        return true;
      }
  
      // Check the status of the control
      return control.status === 'VALID';
    });
  }

  hasSampleSource(field: any): boolean {
    return field.values.some((value: any) => value.labelCode === PropertyName.SampleSource);
  }

  subscribeToTestsProfilesSelectionChange(): void {
    
      const testsControl = this.form.get(PropertyName.TESTS) as UntypedFormArray;
      const profilesControl = this.form.get(PropertyName.PROFILES) as UntypedFormArray;
      // Subscribe to changes in the TESTS checkbox list
      if (testsControl) {
        testsControl.valueChanges.subscribe(() => {
          this.TestsProfilesSelectionChange.emit();
        });
      }
      // Subscribe to changes in the PROFILES checkbox list
      if (profilesControl) {
        profilesControl.valueChanges.subscribe(() => {
          this.TestsProfilesSelectionChange.emit();
        });
      }
  }

}
